import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Polygon, MultiPolygon, Geometry } from 'ol/geom';
import { Fill, Stroke, Style} from 'ol/style';
import { Feature } from 'ol';
import { ZoneFeature } from '../models/zones-base-interfaces';
import { formatFromTo, getToolTipText } from './utils';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface ZoneColors
{
    borderColor: string;
    fillColor: string;
    restricted?: string;
}

export abstract class ZoneLayerBase extends EventTarget
{
    abstract getZones(): Promise<ZoneFeature[]>;
    abstract zoneId: string;
    
    private _layer : VectorLayer<VectorSource<Geometry>> | null = null;

    constructor(colors?: Partial<ZoneColors>)
    {
        super();
        this.setColors(colors || {});
    }

    get colors(): ZoneColors
    {
        return this._colors;
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        this._vectorSource = new VectorSource();
        this._layer = new VectorLayer({source: this._vectorSource, visible: false});
        this._layers.push(this._layer);
    }

    setColors(colors: Partial<ZoneColors>): void
    {
        this._colors.borderColor = colors.borderColor || this.colors.borderColor;
        this._colors.fillColor = colors.fillColor || this.colors.fillColor;

        if (this._vectorSource){
            this._clear();
            this.plot();
        }
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    enable() {
        this._clear();
        this.plot().then(() => this._layer!.setVisible(true));
    }

    disable() {
        this._layer!.setVisible(false);
        this._clear();
    }

    private _colors: ZoneColors = {
        borderColor: "black",
        fillColor: "black",
        restricted: "red"
    };

    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _vectorSource: VectorSource<Geometry>|null = null;

    // A polygon whose coordinates straddle the antimeridian (±180° longitude)
    // otherwise renders as a band stretching across the whole map. Keeping the
    // longitudes continuous (shifting by ±360° on jumps > 180°) fixes that.
    // No-op for polygons that don't cross the antimeridian.
    private _normalizeRing = (coords: number[][]): number[][] => {
        if (coords.length < 2) return coords;
        const normalized = [coords[0]];
        for (let i = 1; i < coords.length; i++) {
            const [lon, lat] = coords[i];
            const prevLon = normalized[i - 1][0];
            const lonDiff = lon - prevLon;
            let adjustedLon = lon;
            if (lonDiff > 180) {
                adjustedLon = lon - 360;
            } else if (lonDiff < -180) {
                adjustedLon = lon + 360;
            }
            normalized.push([adjustedLon, lat]);
        }
        return normalized;
    };

    private _normalizePolygonCoords = (coords: number[][][]): number[][][] =>
        coords.map(ring => this._normalizeRing(ring));

    private _normalizeMultiPolygonCoords = (coords: number[][][][]): number[][][][] =>
        coords.map(polygon => this._normalizePolygonCoords(polygon));

    public async plot() {
        this._clear();

        const zoneFeatures = await this.getZones();
        if (!zoneFeatures) {
            return;
        }

        const features: Feature<Geometry>[] = [];
        const fillColor = this.colors.fillColor || "black";
        const borderColor = this.colors.borderColor || "black";
        const restricedColor = this.colors.restricted || "red";

        const zoneStyle = new Style({
            stroke: new Stroke({
                color: borderColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        const zoneStyleRestricted = new Style({
            stroke: new Stroke({
                color: restricedColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });
        
        let style = zoneStyle;
        let prefix = "";

        zoneFeatures!.forEach(z => {
            let feature: Feature<Geometry>;
            if (z.geofence!.type === "Polygon") {
                const coords = this._normalizePolygonCoords(<number[][][]>z.geofence!.coordinates);
                feature = new Feature({geometry: new Polygon(coords)});
            }
            if (z.geofence!.type === "MultiPolygon") {
                const coords = this._normalizeMultiPolygonCoords(<number[][][][]>z.geofence!.coordinates);
                feature = new Feature({geometry: new MultiPolygon(coords)});
            }

            if (z.properties?.[0].isNoGoZone) {
                    style = zoneStyleRestricted;
                }

            feature!.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            feature!.setStyle(style);
            
            const text = getToolTipText(z);
            feature!.set('text', text);
            feature!.set('layer', this.zoneId);
            features.push(feature!);
        })      

        this._vectorSource!.addFeatures(features);
    }
};